import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink,
  BarChart3,
  Trash2,
  Copy,
  Check,
  Plus,
  QrCode,
  Share2,
  Search,
  Bug,
  Link as LinkIcon,
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { linksAPI } from '../services/api';
import { copyToClipboard, formatDate } from '../utils/helpers';
import { SHORT_LINK_BASE_URL } from '../config';
import {
  LoadingSpinner,
  Alert,
  Table,
  Badge,
  EmptyState,
  Tooltip,
  Modal,
} from './common';
import QRCodeGenerator from './QRCodeGenerator';
import SocialSharing from './SocialSharing';

const LinkList = () => {
  const { loading, error, executeApiCall, clearError } = useApi();
  const [links, setLinks] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    const result = await executeApiCall(linksAPI.getAll);
    if (result.success) {
      const linksData = result.data.links || result.data || [];
      setLinks(Array.isArray(linksData) ? linksData : []);
    }
  };

  const handleCopyToClipboard = async (url, id) => {
    const result = await copyToClipboard(url);
    if (result.success) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const deleteLink = async (shortCode) => {
    if (window.confirm('Delete this link? This cannot be undone.')) {
      const result = await executeApiCall(linksAPI.delete, shortCode);
      if (result.success) fetchLinks();
    }
  };

  const hasMobileConfig = (link) =>
    link.android_package_name || link.ios_bundle_id;

  const filteredLinks = useMemo(() => {
    if (!query.trim()) return links;
    const q = query.toLowerCase();
    return links.filter(
      (l) =>
        l.short_code?.toLowerCase().includes(q) ||
        l.original_url?.toLowerCase().includes(q) ||
        l.title?.toLowerCase().includes(q)
    );
  }, [links, query]);

  const totals = useMemo(() => {
    const total = links.length;
    const clicks = links.reduce((s, l) => s + (l.clicks || 0), 0);
    const mobile = links.filter(hasMobileConfig).length;
    return { total, clicks, mobile };
  }, [links]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" text="Loading your links…" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
            Your links
          </h1>
          <p className="mt-1.5 text-sm text-ink-500">
            Every short link you've created, with live click counts.
          </p>
        </div>
        <Link
          to="/create"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-medium text-white shadow-soft hover:bg-ink-800 transition self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          Create link
        </Link>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={clearError}
          className="mb-5"
        />
      )}

      {/* Stat strip */}
      {links.length > 0 && (
        <div className="grid grid-cols-3 gap-px rounded-2xl border border-ink-200 bg-ink-100 overflow-hidden shadow-soft mb-6">
          {[
            { label: 'Total links', value: totals.total },
            { label: 'Total clicks', value: totals.clicks.toLocaleString() },
            { label: 'With mobile config', value: totals.mobile },
          ].map((s) => (
            <div key={s.label} className="bg-white px-5 py-4">
              <p className="text-xs uppercase tracking-wider text-ink-500">
                {s.label}
              </p>
              <p className="mt-1 text-xl font-bold tracking-tight text-ink-900">
                {s.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {links.length === 0 ? (
        <EmptyState
          icon={LinkIcon}
          title="No links yet"
          description="Create your first dynamic link to get a short URL, deep link and analytics."
          action={
            <Link
              to="/create"
              className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-medium text-white shadow-soft hover:bg-ink-800 transition"
            >
              <Plus className="h-4 w-4" />
              Create your first link
            </Link>
          }
        />
      ) : (
        <>
          {/* Search */}
          <div className="mb-4 relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by URL, code or title…"
              className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-3 py-2 text-sm placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
            />
          </div>

          <Table>
            <Table.Head>
              <tr>
                <Table.Header>Short link</Table.Header>
                <Table.Header>Destination</Table.Header>
                <Table.Header>Mobile</Table.Header>
                <Table.Header>Clicks</Table.Header>
                <Table.Header>Created</Table.Header>
                <Table.Header className="text-right pr-5">Actions</Table.Header>
              </tr>
            </Table.Head>
            <Table.Body>
              {filteredLinks.map((link) => {
                const shortUrl = `${SHORT_LINK_BASE_URL}/r/${link.short_code}`;
                const debugUrl = `${shortUrl}?d=1`;
                return (
                  <Table.Row key={link.id}>
                    <Table.Cell>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-brand-50 text-brand-600 shrink-0">
                          <LinkIcon className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0">
                          {link.title && (
                            <p className="text-sm font-medium text-ink-900 truncate">
                              {link.title}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5">
                            <a
                              href={shortUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-xs text-brand-600 hover:text-brand-700 truncate"
                            >
                              /r/{link.short_code}
                            </a>
                            <Tooltip content={copiedId === link.id ? 'Copied!' : 'Copy short URL'}>
                              <button
                                onClick={() =>
                                  handleCopyToClipboard(shortUrl, link.id)
                                }
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-ink-400 hover:text-ink-700 hover:bg-ink-100"
                              >
                                {copiedId === link.id ? (
                                  <Check className="h-3.5 w-3.5 text-emerald-600" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" />
                                )}
                              </button>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <a
                        href={link.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-1.5 text-sm text-ink-700 hover:text-brand-700 max-w-xs"
                      >
                        <span className="truncate">{link.original_url}</span>
                        <ExternalLink className="h-3 w-3 text-ink-400 group-hover:text-brand-600 shrink-0" />
                      </a>
                    </Table.Cell>
                    <Table.Cell>
                      {hasMobileConfig(link) ? (
                        <Badge variant="success" dot>
                          {link.android_package_name && link.ios_bundle_id
                            ? 'iOS + Android'
                            : link.android_package_name
                            ? 'Android'
                            : 'iOS'}
                        </Badge>
                      ) : (
                        <Badge variant="default">Web only</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <span className="inline-flex items-center gap-1 font-semibold text-ink-900">
                        {(link.clicks || 0).toLocaleString()}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="text-sm text-ink-500">
                      {formatDate(link.created_at)}
                    </Table.Cell>
                    <Table.Cell className="text-right pr-3">
                      <div className="inline-flex items-center gap-0.5">
                        <Tooltip content="Analytics">
                          <Link
                            to={`/analytics/${link.short_code}`}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-brand-600 hover:bg-brand-50"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Link>
                        </Tooltip>
                        <Tooltip content="QR code">
                          <button
                            onClick={() => {
                              setSelectedLink(link);
                              setShowQRModal(true);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-ink-900 hover:bg-ink-100"
                          >
                            <QrCode className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Share">
                          <button
                            onClick={() => {
                              setSelectedLink(link);
                              setShowShareModal(true);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-violet-600 hover:bg-violet-50"
                          >
                            <Share2 className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip
                          content={
                            copiedId === `debug-${link.id}`
                              ? 'Copied debug URL!'
                              : 'Copy debug URL (?d=1)'
                          }
                        >
                          <button
                            onClick={() =>
                              handleCopyToClipboard(debugUrl, `debug-${link.id}`)
                            }
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-amber-600 hover:bg-amber-50"
                          >
                            {copiedId === `debug-${link.id}` ? (
                              <Check className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <Bug className="h-4 w-4" />
                            )}
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete">
                          <button
                            onClick={() => deleteLink(link.short_code)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-500 hover:text-rose-600 hover:bg-rose-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
              {filteredLinks.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={6} className="py-10">
                    <div className="text-center text-sm text-ink-500">
                      No links match "{query}".
                    </div>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </>
      )}

      <Modal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        title="QR code"
        description="Download or print a scannable code for this link."
        size="lg"
      >
        {selectedLink && (
          <QRCodeGenerator
            shortCode={selectedLink.short_code}
            linkTitle={selectedLink.title}
            linkDescription={selectedLink.description}
          />
        )}
      </Modal>

      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Share this link"
        description="Send your short link wherever your audience is."
        size="lg"
      >
        {selectedLink && (
          <SocialSharing
            shortCode={selectedLink.short_code}
            linkTitle={selectedLink.title}
            linkDescription={selectedLink.description}
          />
        )}
      </Modal>
    </div>
  );
};

export default LinkList;
